import { Building2, ChevronsUpDown, LogOut, Monitor, Moon, Settings, Sun, User } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Avatar from '@/components/primitives/Avatar.jsx';
import Icon from '@/components/primitives/Icon.jsx';
import Menu from '@/components/patterns/Menu.jsx';
import getWorkspaceLabel from '@/lib/company/getWorkspaceLabel.js';
import useThemePreference from '@/lib/theme/useThemePreference.js';
import toastManager from '@/lib/toast/toastManager.js';
import signOutAndClear from '@/redux/actions/sessionActions/signOutAndClear.js';
import switchOrganization from '@/redux/actions/sessionActions/switchOrganization.js';
import { activeCompany, authSessionUser, userCompanies } from '@/redux/selectors/userSelectors.js';

const THEME_OPTIONS = [
  { value: 'system', label: 'Match system', icon: Monitor },
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon }
];

/** Who is signed in and where, with the workspace switch, the theme choice, settings and sign out. Sits at the foot of the sidebar. */
const AccountMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(authSessionUser);
  const company = useSelector(activeCompany);
  const companies = useSelector(userCompanies);
  const [themePreference, setThemePreference] = useThemePreference();
  const displayName = user?.name || user?.email || 'Your account';
  const organizationName = getWorkspaceLabel(company);
  const workspaceOptions = companies.map((item) => ({ value: item.id, label: getWorkspaceLabel(item), icon: item.isPersonal ? User : Building2 }));

  /* The page open now belongs to the workspace being left, so the switch starts from home: a
     conversation or automation left open would be read again under the new workspace and not found. */
  const handleSwitchWorkspace = async (companyId) => {
    if (!companyId || companyId === company?.id) {
      return;
    }

    navigate('/');
    const res = await dispatch(switchOrganization({ companyId }));

    if (res?.success === false) {
      toastManager.add({ type: 'error', title: 'Could not switch workspace', description: res.message });
    }
  };

  const handleSignOut = async () => {
    await dispatch(signOutAndClear());
    navigate('/sign-in', { replace: true });
  };

  return (
    <Menu
      side="top"
      trigger={
        <button type="button" className="comp-app-shell__account" aria-label={`Account menu for ${displayName}`}>
          <Avatar name={displayName} size="sm" />
          <span className="comp-app-shell__account-text">
            <span className="comp-app-shell__account-name">{displayName}</span>
            {organizationName && <span className="comp-app-shell__account-org">{organizationName}</span>}
          </span>
          <Icon icon={ChevronsUpDown} />
        </button>
      }>
      <Menu.Header>
        <span className="comp-app-shell__menu-name">{user?.name || displayName}</span>
        {user?.email && <span className="comp-app-shell__menu-email">{user.email}</span>}
      </Menu.Header>
      <Menu.Separator />
      {workspaceOptions.length > 1 && (
        <>
          <Menu.RadioGroup label="Workspace" value={company?.id} onValueChange={handleSwitchWorkspace} options={workspaceOptions} closeOnClick />
          <Menu.Separator />
        </>
      )}
      <Menu.RadioGroup label="Theme" value={themePreference} onValueChange={setThemePreference} options={THEME_OPTIONS} />
      <Menu.Separator />
      <Menu.Item icon={Settings} onClick={() => navigate('/settings')}>
        Settings
      </Menu.Item>
      <Menu.Item icon={LogOut} onClick={handleSignOut}>
        Sign out
      </Menu.Item>
    </Menu>
  );
};

export default AccountMenu;
